import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Newspaper, Swords, Megaphone, Plus, Trash2, Pencil, Trophy, Play } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, Empty, btn, Pagination } from "@/components/Page";
import BattleReplay from "@/components/BattleReplay";

// Custom News Icon for PageHeader
const NewsIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/news-icon.png" 
    alt="News & Updates" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

function TodaysFights() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [replayId, setReplayId] = useState(null);
  useEffect(() => {
    const load = () => api.get("/fights/recent").then((r) => setData(r.data)).catch(() => setData({ fights: [], count: 0 }));
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  if (data === null) return <Loader label="Tallying recent bouts…" />;

  return (
    <div>
      <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-4 flex items-center gap-2">
        <Swords size={16} /> Recent Fights <span className="text-zinc-600 normal-case tracking-normal">({data.count})</span>
      </h2>
      {data.fights.length === 0 ? (
        <Empty label="No fights have taken place yet. Send a challenge to start the bloodshed." />
      ) : (
        <div className="space-y-2" data-testid="recent-fights">
          {data.fights.map((f) => {
            const g1Won = f.winner_id && f.winner_id === f.g1_id;
            return (
              <div key={f.battle_id} data-testid={`today-fight-${f.battle_id}`}
                className="flex items-center gap-3 border border-zinc-800 bg-zinc-950/60 px-4 py-3 hover:border-amber-900/40 transition-colors">
                <Trophy size={15} className="text-amber-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate">
                    {f.g1_id ? <button onClick={() => navigate(`/gladiator/${f.g1_id}`)} className={g1Won ? "text-emerald-400 hover:text-amber-400" : "text-zinc-300 hover:text-amber-400"}>{f.g1}</button> : <span>{f.g1}</span>}
                    <span className="text-zinc-600 mx-1.5">vs</span>
                    {f.g2_id ? <button onClick={() => navigate(`/gladiator/${f.g2_id}`)} className={!g1Won ? "text-emerald-400 hover:text-amber-400" : "text-zinc-300 hover:text-amber-400"}>{f.g2}</button> : <span>{f.g2}</span>}
                  </div>
                  {f.headline && <div className="text-[11px] text-zinc-500 italic truncate">{f.headline}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-amber-400/80 font-cinzel">{f.winner_name} won</div>
                  <div className="text-[10px] text-zinc-600">{f.rounds} rds · {fmtTime(f.created_at)}</div>
                </div>
                <button data-testid={`replay-fight-${f.battle_id}`} onClick={() => setReplayId(f.battle_id)} title="Watch replay"
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wide font-cinzel text-amber-300 border border-amber-900/50 bg-amber-950/20 hover:bg-amber-900/30 hover:border-amber-600 transition-colors">
                  <Play size={12} /> Watch
                </button>
              </div>
            );
          })}
        </div>
      )}
      <BattleReplay battleId={replayId} open={!!replayId} onClose={() => setReplayId(null)} shareable />
    </div>
  );
}

function NewsSection() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 5;

  const load = () => api.get("/news").then((r) => setData(r.data)).catch(() => setData({ news: [], is_admin: false }));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    if (open && !editId) { setOpen(false); return; }
    setEditId(null); setTitle(""); setBody(""); setOpen(true);
  };
  const startEdit = (n) => {
    setEditId(n.id); setTitle(n.title); setBody(n.body); setOpen(true);
  };
  const cancelForm = () => { setOpen(false); setEditId(null); setTitle(""); setBody(""); };

  const post = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body are required");
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/news/${editId}`, { title: title.trim(), body: body.trim() });
        toast.success("Update edited");
      } else {
        await api.post("/news", { title: title.trim(), body: body.trim() });
        toast.success("Update published");
        setPage(1);
      }
      cancelForm(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try { await api.delete(`/news/${id}`); if (editId === id) cancelForm(); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  if (data === null) return <Loader />;
  const isAdmin = data.is_admin || user?.is_admin;
  const totalPages = Math.max(1, Math.ceil(data.news.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const pageItems = data.news.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2">
          <Megaphone size={16} /> News & Updates
        </h2>
        {isAdmin && (
          <button data-testid="add-news-btn" onClick={startCreate} className={btn("ghost") + " py-1.5 px-3 flex items-center gap-1.5"}>
            <Plus size={14} /> {open && !editId ? "Cancel" : "Add Update"}
          </button>
        )}
      </div>

      {isAdmin && open && (
        <div className="border border-amber-900/40 bg-zinc-950/60 p-4 mb-5 space-y-3" data-testid="news-form">
          {editId && <div className="text-[11px] uppercase tracking-widest text-amber-400/80" data-testid="news-edit-banner">Editing update · readers won't be re-alerted</div>}
          <input data-testid="news-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} placeholder="Update title"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
          <textarea data-testid="news-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="What changed in the arena?"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none resize-y" />
          <div className="flex items-center gap-2">
            <button data-testid="publish-news-btn" disabled={busy} onClick={post} className={btn() + " py-2 px-5"}>{editId ? "Save changes" : "Publish"}</button>
            {editId && <button data-testid="cancel-edit-btn" onClick={cancelForm} className={btn("ghost") + " py-2 px-4"}>Cancel</button>}
          </div>
        </div>
      )}

      {data.news.length === 0 ? (
        <Empty label="No updates yet. Check back soon for patch notes and arena announcements." />
      ) : (
        <>
        <div className="space-y-3" data-testid="news-list">
          {pageItems.map((n) => (
            <div key={n.id} data-testid={`news-${n.id}`} className="border border-zinc-800 bg-zinc-950/60 p-4">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-cinzel text-amber-200 tracking-wide">{n.title}</h3>
                {isAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`edit-news-${n.id}`} onClick={() => startEdit(n)} className="text-zinc-600 hover:text-amber-400 transition-colors" title="Edit update">
                      <Pencil size={14} />
                    </button>
                    <button data-testid={`delete-news-${n.id}`} onClick={() => remove(n.id)} className="text-zinc-600 hover:text-red-400 transition-colors" title="Delete update">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-sm text-zinc-300 mt-1.5 whitespace-pre-wrap leading-relaxed">{n.body}</p>
              <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-2.5">
                {n.author} · {fmtDate(n.created_at)}{n.updated_at && <span className="text-zinc-700 normal-case tracking-normal italic"> · edited {fmtDate(n.updated_at)}</span>}
              </div>
            </div>
          ))}
        </div>
        {totalPages > 1 && (
          <Pagination page={curPage} totalPages={totalPages} onChange={setPage} testid="news" prevLabel="Newer" nextLabel="Older" className="mt-5" />
        )}
        </>
      )}
    </div>
  );
}

export default function News() {
  return (
    <div>
      <PageHeader title="News & Updates" subtitle="Recent arena results and the latest game updates" icon={NewsIcon} />
      <div className="space-y-10">
        <NewsSection />
        <TodaysFights />
      </div>
    </div>
  );
}
